import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  ArrowLeft, Upload, Plus, X, Image as ImageIcon, Trash2, Save,
  Search, Link, Unlink, Package, Timer, Camera, ChevronUp, ChevronDown,
  Pencil, Clock, Leaf, List, Hash, BarChart2, AlertTriangle,
} from 'lucide-react';
import { Card, Button } from '../UI';
import { submitRecipe, updateRecipe, saveDraft, loadDraft, deleteDraft, hasDraftSaved } from '../../hooks/useRecipes';
import { collection, query, getDocs, orderBy, limit, where } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { uploadImageToR2 } from '../../services/r2UploadService';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const MEAL_TYPES   = ['Breakfast', 'Lunch', 'Dinner', 'Snack', 'Dessert', 'Beverage'];
const CUISINES     = ['American', 'Italian', 'Mexican', 'Chinese', 'Japanese', 'Thai', 'Indian', 'French', 'Mediterranean', 'Middle Eastern', 'Greek', 'Spanish', 'Korean', 'Vietnamese', 'Caribbean', 'African', 'British', 'German', 'Brazilian', 'Fusion', 'Other'];
const DIFFICULTIES = ['Easy', 'Medium', 'Hard'];
const DIETARY_TAGS = ['Vegan', 'Vegetarian', 'Pescatarian', 'Gluten-Free', 'Dairy-Free', 'Keto', 'Paleo', 'Low-Carb', 'Nut-Free', 'Halal', 'Kosher', 'High-Protein'];
const ALLERGENS    = ['Gluten', 'Dairy', 'Eggs', 'Nuts', 'Peanuts', 'Soy', 'Fish', 'Shellfish', 'Sesame', 'Wheat'];
const RECIPE_TAGS  = [
  { value: 'quickEasy',      label: 'Quick & Easy'    },
  { value: 'onePot',         label: 'One-Pot'         },
  { value: 'mealPrep',       label: 'Meal Prep'       },
  { value: 'kidFriendly',    label: 'Kid-Friendly'    },
  { value: 'budgetFriendly', label: 'Budget-Friendly' },
  { value: 'highProtein',    label: 'High-Protein'    },
  { value: 'lowCalorie',     label: 'Low-Calorie'     },
  { value: 'comfort',        label: 'Comfort Food'    },
  { value: 'healthy',        label: 'Healthy'         },
];

const INPUT_CLASS = 'w-full px-lg py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 text-body text-text-primary';

// ---------------------------------------------------------------------------
// FormCard — matches iOS formCard: icon badge + title + optional subtitle
// ---------------------------------------------------------------------------

const FormCard = ({ title, icon: Icon, subtitle, children }) => (
  <div
    className="rounded-large bg-surface p-lg"
    style={{
      border: '1px solid rgba(var(--color-primary-rgb),0.07)',
      boxShadow: '0 2px 6px rgba(0,0,0,0.04)',
    }}
  >
    <div className="flex items-start gap-sm mb-lg">
      <div
        className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: 'rgba(var(--color-primary-rgb),0.1)' }}
      >
        <Icon size={13} className="text-primary" />
      </div>
      <div>
        <p className="text-callout font-bold text-text-primary leading-tight">{title}</p>
        {subtitle && <p className="text-caption text-text-tertiary mt-xs">{subtitle}</p>}
      </div>
    </div>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// FieldLabel
// ---------------------------------------------------------------------------

const FieldLabel = ({ children }) => (
  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-xs">{children}</p>
);

// ---------------------------------------------------------------------------
// ProductLinkModal
// ---------------------------------------------------------------------------

const ProductLinkModal = ({ ingredientName, onLink, onClose }) => {
  const [searchText, setSearchText] = useState(ingredientName || '');
  const [results, setResults]       = useState([]);
  const [loading, setLoading]       = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
    if (searchText.trim().length >= 2) runSearch(searchText);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const runSearch = async (q) => {
    if (!q.trim() || q.trim().length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const snap = await getDocs(
        query(collection(db, 'products'), where('isVerified', '==', true), orderBy('name'), limit(30))
      );
      const all  = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      const term = q.toLowerCase();
      setResults(
        all.filter(p =>
          (p.name || '').toLowerCase().includes(term) ||
          (p.brand || '').toLowerCase().includes(term)
        ).slice(0, 10)
      );
    } catch (err) {
      console.error('Product search error:', err);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    const val = e.target.value;
    setSearchText(val);
    if (val.trim().length >= 2) runSearch(val); else setResults([]);
  };

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-lg">
      <div className="bg-background rounded-large border border-text-tertiary/20 w-full max-w-lg shadow-xl">
        <div className="p-lg border-b border-text-tertiary/10 flex items-center justify-between">
          <div>
            <h3 className="text-title-3 font-semibold text-primary">Link to Product</h3>
            <p className="text-caption text-text-secondary mt-xs">Search the Nutrius catalog to link to this ingredient</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full hover:bg-surface flex items-center justify-center">
            <X size={18} className="text-text-tertiary" />
          </button>
        </div>
        <div className="p-lg">
          <div className="relative mb-lg">
            <Search size={16} className="absolute left-md top-1/2 -translate-y-1/2 text-text-tertiary" />
            <input
              ref={inputRef}
              type="text"
              value={searchText}
              onChange={handleSearch}
              placeholder="Search by name or brand..."
              className="w-full pl-xxxl pr-md py-md bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none text-body"
            />
          </div>
          <div className="space-y-sm max-h-80 overflow-y-auto">
            {loading && (
              <div className="text-center py-lg">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
              </div>
            )}
            {!loading && results.length === 0 && searchText.trim().length >= 2 && (
              <p className="text-center text-caption text-text-secondary py-lg">No products found for "{searchText}"</p>
            )}
            {!loading && searchText.trim().length < 2 && (
              <p className="text-center text-caption text-text-secondary py-lg">Type at least 2 characters to search</p>
            )}
            {results.map(product => (
              <button
                key={product.id}
                onClick={() => onLink(product)}
                className="w-full flex items-center gap-md p-md rounded-medium hover:bg-primary/5 border border-transparent hover:border-primary/20 transition-all text-left"
              >
                <div className="w-12 h-12 rounded-medium bg-surface border border-text-tertiary/10 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {product.imageURL
                    ? <img src={product.imageURL} alt={product.name} className="w-full h-full object-contain p-xs" />
                    : <Package size={20} className="text-text-tertiary" />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-callout font-semibold text-text-primary truncate">{product.name}</p>
                  {product.brand && <p className="text-caption text-text-secondary">{product.brand}</p>}
                  {product.isVerified && (
                    <p className="text-caption text-primary mt-xs">Verified product</p>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ---------------------------------------------------------------------------
// StepPhotoUpload
// ---------------------------------------------------------------------------

const StepPhotoUpload = ({ stepId, currentImageURL, onUploaded, entityId }) => {
  const inputRef                    = useRef(null);
  const [uploading, setUploading]   = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await uploadImageToR2(file, 'recipe', entityId || 'draft', `step_${stepId}`);
      onUploaded(result.urls.medium);
    } catch (err) {
      console.error('Step photo upload error:', err);
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  if (currentImageURL) {
    return (
      <div className="relative mt-md rounded-medium overflow-hidden">
        <img src={currentImageURL} alt="Step" className="w-full h-44 object-cover" />
        <div className="absolute top-sm right-sm flex gap-xs">
          <button
            onClick={() => inputRef.current?.click()}
            className="flex items-center gap-xs px-sm py-xs bg-black/60 rounded-medium text-white text-xs font-semibold hover:bg-black/80 transition-all"
          >
            <Upload size={12} />
            Change
          </button>
          <button
            onClick={() => onUploaded(null)}
            className="w-7 h-7 rounded-full bg-error flex items-center justify-center text-white hover:bg-error/80 transition-all"
          >
            <X size={13} />
          </button>
        </div>
        <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className="mt-md flex items-center gap-xs px-md py-sm rounded-medium border border-dashed border-text-tertiary/30 text-caption text-text-tertiary hover:border-primary hover:text-primary transition-all disabled:opacity-50"
      >
        {uploading
          ? <><div className="w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin" />Uploading...</>
          : <><Camera size={14} />Add step photo</>
        }
      </button>
      <input ref={inputRef} type="file" accept="image/*" onChange={handleFile} className="hidden" />
    </>
  );
};

// ---------------------------------------------------------------------------
// EditableStepCard
// ---------------------------------------------------------------------------

const EditableStepCard = ({ step, index, total, entityId, onUpdate, onRemove, onMoveUp, onMoveDown }) => (
  <div
    className="rounded-large overflow-hidden"
    style={{ border: '1px solid rgba(var(--color-text-tertiary),0.12)' }}
  >
    {step.imageURL && (
      <div className="w-full aspect-video overflow-hidden bg-surface">
        <img src={step.imageURL} alt={`Step ${step.order}`} className="w-full h-full object-cover" />
      </div>
    )}

    <div className="p-lg">
      <div className="flex items-center gap-md mb-md">
        <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
          <span className="text-caption font-bold text-white">{step.order}</span>
        </div>
        <span className="text-callout font-semibold text-text-primary flex-1">Step {step.order}</span>

        <button
          onClick={() => onMoveUp(index)}
          disabled={index === 0}
          className="w-7 h-7 rounded-medium bg-surface border border-text-tertiary/15 flex items-center justify-center text-text-tertiary hover:text-primary hover:border-primary/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronUp size={14} />
        </button>
        <button
          onClick={() => onMoveDown(index)}
          disabled={index === total - 1}
          className="w-7 h-7 rounded-medium bg-surface border border-text-tertiary/15 flex items-center justify-center text-text-tertiary hover:text-primary hover:border-primary/30 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronDown size={14} />
        </button>
        <button
          onClick={() => onRemove(step.id)}
          className="w-7 h-7 rounded-full bg-error/10 hover:bg-error/20 flex items-center justify-center transition-colors"
        >
          <Trash2 size={13} className="text-error" />
        </button>
      </div>

      <textarea
        value={step.instruction}
        onChange={e => onUpdate(step.id, { instruction: e.target.value })}
        placeholder="Describe this step..."
        rows={3}
        className="w-full px-md py-sm bg-surface border border-text-tertiary/20 rounded-medium focus:border-primary focus:outline-none resize-none text-body text-text-primary"
      />

      <div className="flex items-center gap-md mt-md">
        <label className="flex items-center gap-sm cursor-pointer select-none">
          <input
            type="checkbox"
            checked={!!step.timerMinutes}
            onChange={e => onUpdate(step.id, { timerMinutes: e.target.checked ? (step.timerMinutes || 5) : null })}
            className="accent-primary"
          />
          <Timer size={14} className="text-text-tertiary" />
          <span className="text-caption text-text-secondary">Timer</span>
        </label>
        {!!step.timerMinutes && (
          <div className="flex items-center gap-sm">
            <input
              type="number"
              value={step.timerMinutes}
              onChange={e => onUpdate(step.id, { timerMinutes: parseInt(e.target.value) || null })}
              min="1"
              className="w-20 px-md py-xs bg-surface border border-text-tertiary/20 rounded-small focus:border-primary focus:outline-none text-body text-text-primary"
            />
            <span className="text-caption text-text-tertiary">min</span>
          </div>
        )}
      </div>

      <StepPhotoUpload
        stepId={step.id}
        currentImageURL={step.imageURL}
        entityId={entityId}
        onUploaded={(url) => onUpdate(step.id, { imageURL: url })}
      />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const RecipeSubmissionView = ({ onBack, existingRecipe }) => {
  const isEditing     = !!existingRecipe;
  const imageInputRef = useRef(null);
  const entityId      = existingRecipe?.firestoreId || existingRecipe?.id || `draft_${Date.now()}`;

  // ── Basic info ──────────────────────────────────────────────────────────────
  const [title, setTitle]             = useState(existingRecipe?.title || '');
  const [description, setDescription] = useState(existingRecipe?.description || '');
  const [servings, setServings]       = useState(existingRecipe?.servings?.toString() || '');
  const [prepTime, setPrepTime]       = useState((existingRecipe?.prepTimeMinutes || existingRecipe?.prepTime)?.toString() || '');
  const [cookTime, setCookTime]       = useState((existingRecipe?.cookTimeMinutes || existingRecipe?.cookTime)?.toString() || '');
  const [mealType, setMealType]       = useState(existingRecipe?.mealType || '');
  const [cuisine, setCuisine]         = useState(existingRecipe?.cuisine || '');
  const [difficulty, setDifficulty]   = useState(existingRecipe?.difficulty || '');
  const [dietaryTags, setDietaryTags] = useState(existingRecipe?.dietaryTags || []);
  const [recipeTags, setRecipeTags]   = useState(existingRecipe?.recipeTags || []);
  const [allergens, setAllergens]     = useState(existingRecipe?.allergens || []);

  // ── Cover image ─────────────────────────────────────────────────────────────
  const [imageFile, setImageFile]       = useState(null);
  const [imagePreview, setImagePreview] = useState(
    existingRecipe ? (existingRecipe.imageURL || existingRecipe.imageUrl || null) : null
  );

  // ── Ingredients ─────────────────────────────────────────────────────────────
  const [ingredients, setIngredients]                 = useState(
    (existingRecipe?.ingredientsList || existingRecipe?.ingredients || [])
      .map(i => ({ ...i, id: i.id || Date.now().toString() + Math.random() }))
  );
  const [showIngredientInput, setShowIngredientInput] = useState(false);
  const [ingredientName, setIngredientName]           = useState('');
  const [ingredientAmount, setIngredientAmount]       = useState('');
  const [ingredientUnit, setIngredientUnit]           = useState('');
  const [ingredientNotes, setIngredientNotes]         = useState('');
  const [linkingIngredientId, setLinkingIngredientId] = useState(null);

  // ── Steps ───────────────────────────────────────────────────────────────────
  const [steps, setSteps] = useState(
    (existingRecipe?.stepsList || existingRecipe?.steps || []).map((s, i) => ({
      id:           s.id          || `${Date.now()}_${i}`,
      order:        s.order       || s.stepNumber || i + 1,
      stepNumber:   s.order       || s.stepNumber || i + 1,
      instruction:  s.instruction || s.text       || '',
      timerMinutes: s.timerMinutes ?? null,
      imageURL:     s.imageURL    || s.imageUrl   || null,
    }))
  );
  const [showStepInput, setShowStepInput]     = useState(false);
  const [stepInstruction, setStepInstruction] = useState('');
  const [stepTimer, setStepTimer]             = useState('');
  const [stepHasTimer, setStepHasTimer]       = useState(false);

  // ── Nutrition ────────────────────────────────────────────────────────────────
  const [nutrition, setNutrition] = useState({
    calories:      existingRecipe?.nutrition?.calories      ?? null,
    protein:       existingRecipe?.nutrition?.protein       ?? null,
    carbohydrates: existingRecipe?.nutrition?.carbohydrates ?? null,
    fat:           existingRecipe?.nutrition?.fat           ?? null,
    fiber:         existingRecipe?.nutrition?.fiber         ?? null,
    sugar:         existingRecipe?.nutrition?.sugar         ?? null,
    sodium:        existingRecipe?.nutrition?.sodium        ?? null,
  });

  // ── UI state ─────────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting]       = useState(false);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [draftSaved, setDraftSaved]           = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const [error, setError]                     = useState(null);

  useEffect(() => {
    if (!isEditing && hasDraftSaved()) setShowDraftBanner(true);
  }, [isEditing]);

  // ---------------------------------------------------------------------------
  // Draft
  // ---------------------------------------------------------------------------

  const handleLoadDraft = () => {
    const draft = loadDraft();
    if (!draft) return;
    setTitle(draft.title || '');
    setDescription(draft.description || '');
    setServings(draft.servings || '');
    setPrepTime(draft.prepTime || '');
    setCookTime(draft.cookTime || '');
    setMealType(draft.mealType || '');
    setCuisine(draft.cuisine || '');
    setDifficulty(draft.difficulty || '');
    setDietaryTags(draft.dietaryTags || []);
    setRecipeTags(draft.recipeTags || []);
    setAllergens(draft.allergens || []);
    setIngredients(draft.ingredients || []);
    setSteps(draft.steps || []);
    setNutrition(draft.nutrition || { calories: null, protein: null, carbohydrates: null, fat: null, fiber: null, sugar: null, sodium: null });
    if (draft.imagePreview) setImagePreview(draft.imagePreview);
    setShowDraftBanner(false);
  };

  const handleSaveDraft = () => {
    saveDraft({ title, description, servings, prepTime, cookTime, mealType, cuisine, difficulty, dietaryTags, recipeTags, allergens, ingredients, steps, nutrition, imagePreview });
    setDraftSaved(true);
    setTimeout(() => setDraftSaved(false), 2000);
  };

  // ---------------------------------------------------------------------------
  // Cover image
  // ---------------------------------------------------------------------------

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);
  };

  // ---------------------------------------------------------------------------
  // Tags / allergens
  // ---------------------------------------------------------------------------

  const toggleDietaryTag = (tag) =>
    setDietaryTags(prev => prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]);

  const toggleRecipeTag = (value) =>
    setRecipeTags(prev => prev.includes(value) ? prev.filter(t => t !== value) : [...prev, value]);

  const toggleAllergen = (a) =>
    setAllergens(prev => prev.includes(a) ? prev.filter(x => x !== a) : [...prev, a]);

  // ---------------------------------------------------------------------------
  // Ingredients
  // ---------------------------------------------------------------------------

  const addIngredient = () => {
    if (!ingredientName.trim() || !ingredientAmount.trim()) return;
    setIngredients(prev => [...prev, {
      id:                    Date.now().toString(),
      name:                  ingredientName.trim(),
      quantity:              parseFloat(ingredientAmount) || ingredientAmount,
      amount:                ingredientAmount.trim(),
      unit:                  ingredientUnit.trim(),
      notes:                 ingredientNotes.trim() || null,
      isOptional:            false,
      linkedProductId:       null,
      linkedProductName:     null,
      linkedProductImageURL: null,
    }]);
    setIngredientName('');
    setIngredientAmount('');
    setIngredientUnit('');
    setIngredientNotes('');
    setShowIngredientInput(false);
  };

  const removeIngredient = (id) =>
    setIngredients(prev => prev.filter(i => i.id !== id));

  const handleLinkProduct = (ingredientId, product) => {
    setIngredients(prev => prev.map(ing =>
      ing.id === ingredientId
        ? { ...ing, linkedProductId: product.id, linkedProductName: product.name, linkedProductImageURL: product.imageURL || null }
        : ing
    ));
    setLinkingIngredientId(null);
  };

  const handleUnlinkProduct = (ingredientId) =>
    setIngredients(prev => prev.map(ing =>
      ing.id === ingredientId
        ? { ...ing, linkedProductId: null, linkedProductName: null, linkedProductImageURL: null }
        : ing
    ));

  // ---------------------------------------------------------------------------
  // Steps
  // ---------------------------------------------------------------------------

  const addStep = () => {
    if (!stepInstruction.trim()) return;
    setSteps(prev => [...prev, {
      id:           Date.now().toString(),
      order:        prev.length + 1,
      stepNumber:   prev.length + 1,
      instruction:  stepInstruction.trim(),
      timerMinutes: stepHasTimer && stepTimer ? parseInt(stepTimer) : null,
      imageURL:     null,
    }]);
    setStepInstruction('');
    setStepTimer('');
    setStepHasTimer(false);
    setShowStepInput(false);
  };

  const removeStep = (id) =>
    setSteps(prev =>
      prev.filter(s => s.id !== id).map((s, i) => ({ ...s, order: i + 1, stepNumber: i + 1 }))
    );

  const updateStep = (id, patch) =>
    setSteps(prev => prev.map(s => s.id === id ? { ...s, ...patch } : s));

  const moveStep = (index, direction = -1) => {
    setSteps(prev => {
      const next   = [...prev];
      const target = index + direction;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next.map((s, i) => ({ ...s, order: i + 1, stepNumber: i + 1 }));
    });
  };

  // ---------------------------------------------------------------------------
  // Nutrition
  // ---------------------------------------------------------------------------

  const updateNutrition = (field, value) =>
    setNutrition(prev => ({ ...prev, [field]: value === '' ? null : parseFloat(value) }));

  const hasAnyNutrition = Object.values(nutrition).some(v => v != null && v !== '');

  // ---------------------------------------------------------------------------
  // Progress
  // ---------------------------------------------------------------------------

  const progressPercentage = () => {
    let completed = 0;
    if (title)                            completed++;
    if (description)                      completed++;
    if (imagePreview)                     completed++;
    if (servings && prepTime && cookTime) completed++;
    if (mealType && difficulty)           completed++;
    if (dietaryTags.length > 0)           completed++;
    if (ingredients.length > 0)           completed++;
    if (steps.length > 0)                 completed++;
    return Math.round((completed / 8) * 100);
  };

  const isValid = () =>
    title.trim() && description.trim() && servings && prepTime && cookTime &&
    mealType && difficulty && ingredients.length > 0 && steps.length > 0 &&
    (imagePreview || isEditing);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = async () => {
    if (!isValid()) return;
    setIsSubmitting(true);
    setError(null);
    try {
      const formData = {
        title, description, servings, prepTime, cookTime,
        mealType, cuisine, difficulty, dietaryTags, recipeTags, allergens,
        ingredients, steps,
        nutrition: hasAnyNutrition ? nutrition : null,
        imageURL:  existingRecipe?.imageURL || existingRecipe?.imageUrl || null,
        imageUrl:  existingRecipe?.imageUrl || existingRecipe?.imageURL || null,
      };
      if (isEditing) {
        await updateRecipe(existingRecipe.firestoreId || existingRecipe.id, formData, imageFile);
      } else {
        await submitRecipe(formData, imageFile);
        deleteDraft();
      }
      onBack();
    } catch (e) {
      console.error('Submit error:', e);
      setError(e.message || 'Failed to submit recipe. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const linkingIngredient = ingredients.find(i => i.id === linkingIngredientId);
  const linkedCount       = ingredients.filter(i => i.linkedProductId).length;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="max-w-4xl mx-auto space-y-lg">

      {/* ── Header ── */}
      <div>
        <div className="flex items-center justify-between mb-md">
          <button
            onClick={() => title.trim() ? setShowExitConfirm(true) : onBack()}
            className="flex items-center gap-sm text-text-secondary hover:text-primary transition-all"
          >
            <ArrowLeft size={20} />
            <span className="text-callout font-semibold">Back</span>
          </button>
          {!isEditing && (
            <button
              onClick={handleSaveDraft}
              disabled={!title.trim()}
              className="flex items-center gap-xs text-text-secondary hover:text-primary transition-all disabled:opacity-40"
            >
              <Save size={16} />
              <span className="text-caption font-semibold">{draftSaved ? 'Saved!' : 'Save Draft'}</span>
            </button>
          )}
        </div>
        <h1 className="text-display font-rounded text-primary mb-xs">
          {isEditing ? 'Edit Recipe' : 'Create Recipe'}
        </h1>
        <p className="text-body text-text-secondary">
          {isEditing ? 'Update your recipe details' : 'Share your favorite recipe with the community'}
        </p>
      </div>

      {/* ── Progress card — matches iOS progressCard ── */}
      {!isEditing && (
        <div
          className="rounded-large bg-surface p-md"
          style={{ border: '1px solid rgba(var(--color-primary-rgb),0.07)', boxShadow: '0 2px 6px rgba(0,0,0,0.04)' }}
        >
          <div className="flex items-center justify-between mb-sm">
            <p className="text-caption text-text-secondary">Share your recipe with the community</p>
            <p className="text-caption font-semibold text-primary">{progressPercentage()}%</p>
          </div>
          <div className="w-full h-1.5 bg-primary/10 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-300"
              style={{ width: `${progressPercentage()}%` }}
            />
          </div>
        </div>
      )}

      {/* ── Draft banner — matches iOS draftBanner ── */}
      {showDraftBanner && (
        <div
          className="rounded-large p-md flex items-center justify-between"
          style={{ background: 'rgba(var(--color-primary-rgb),0.07)', border: '1px solid rgba(var(--color-primary-rgb),0.15)' }}
        >
          <div className="flex items-center gap-sm">
            <Save size={14} className="text-primary flex-shrink-0" />
            <div>
              <p className="text-callout font-semibold text-text-primary">Continue your draft?</p>
              <p className="text-caption text-text-secondary">You have an unsaved recipe draft.</p>
            </div>
          </div>
          <div className="flex gap-sm flex-shrink-0">
            <Button variant="outline" size="small" onClick={() => { deleteDraft(); setShowDraftBanner(false); }}>Discard</Button>
            <Button variant="primary" size="small" onClick={handleLoadDraft}>Load Draft</Button>
          </div>
        </div>
      )}

      {/* ── Basic Information ── */}
      <FormCard title="Basic Information" icon={Pencil}>
        <div className="space-y-md">

          <div>
            <FieldLabel>Recipe Title *</FieldLabel>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              placeholder="e.g., Grandma's Chocolate Chip Cookies"
              className={INPUT_CLASS}
            />
          </div>

          <div>
            <FieldLabel>Description *</FieldLabel>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="Tell us about this recipe..."
              rows={4}
              className={`${INPUT_CLASS} resize-none`}
            />
          </div>

          <div>
            <FieldLabel>Recipe Photo {!isEditing && '*'}</FieldLabel>
            <input ref={imageInputRef} type="file" accept="image/*" onChange={handleImageChange} className="hidden" />
            {imagePreview ? (
              <div className="relative rounded-medium overflow-hidden">
                <img src={imagePreview} alt="Recipe preview" className="w-full h-64 object-cover" />
                <div className="absolute top-md right-md flex gap-sm">
                  <button
                    onClick={() => imageInputRef.current?.click()}
                    className="px-md py-sm bg-black/60 rounded-medium text-white text-caption font-semibold hover:bg-black/80 transition-all flex items-center gap-xs"
                  >
                    <Upload size={14} />
                    Change
                  </button>
                  <button
                    onClick={() => { setImageFile(null); setImagePreview(null); }}
                    className="w-8 h-8 rounded-full bg-error flex items-center justify-center text-white hover:bg-error/80 transition-all"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ) : (
              <button
                onClick={() => imageInputRef.current?.click()}
                className="w-full h-48 border-2 border-dashed rounded-medium flex flex-col items-center justify-center hover:border-primary transition-colors"
                style={{ borderColor: 'rgba(var(--color-primary-rgb),0.25)', background: 'rgba(var(--color-primary-rgb),0.03)' }}
              >
                <ImageIcon size={40} className="text-text-tertiary mb-md" style={{ opacity: 0.4 }} />
                <p className="text-callout text-text-secondary mb-xs">Tap to upload photo</p>
                <p className="text-caption text-text-tertiary">PNG, JPG up to 10MB</p>
              </button>
            )}
          </div>

        </div>
      </FormCard>

      {/* ── Recipe Details ── */}
      <FormCard title="Recipe Details" icon={Clock}>
        <div className="grid md:grid-cols-2 gap-md">
          <div>
            <FieldLabel>Prep Time (min) *</FieldLabel>
            <input type="number" value={prepTime} onChange={e => setPrepTime(e.target.value)} placeholder="30" min="0" className={INPUT_CLASS} />
          </div>
          <div>
            <FieldLabel>Cook Time (min) *</FieldLabel>
            <input type="number" value={cookTime} onChange={e => setCookTime(e.target.value)} placeholder="45" min="0" className={INPUT_CLASS} />
          </div>
          <div>
            <FieldLabel>Servings *</FieldLabel>
            <input type="number" value={servings} onChange={e => setServings(e.target.value)} placeholder="4" min="1" className={INPUT_CLASS} />
          </div>
          <div>
            <FieldLabel>Difficulty *</FieldLabel>
            <select value={difficulty} onChange={e => setDifficulty(e.target.value)} className={INPUT_CLASS}>
              <option value="">Select difficulty</option>
              {DIFFICULTIES.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <FieldLabel>Meal Type *</FieldLabel>
            <select value={mealType} onChange={e => setMealType(e.target.value)} className={INPUT_CLASS}>
              <option value="">Select meal type</option>
              {MEAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <FieldLabel>Cuisine</FieldLabel>
            <select value={cuisine} onChange={e => setCuisine(e.target.value)} className={INPUT_CLASS}>
              <option value="">Optional</option>
              {CUISINES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </FormCard>

      {/* ── Dietary & Allergens ── */}
      <FormCard title="Dietary & Allergens" icon={Leaf}>
        <div className="space-y-lg">

          {/* Dietary tags — filled primary when selected, matches iOS */}
          <div>
            <FieldLabel>Dietary Tags</FieldLabel>
            <div className="flex flex-wrap gap-sm">
              {DIETARY_TAGS.map(tag => {
                const sel = dietaryTags.includes(tag);
                return (
                  <button
                    key={tag}
                    onClick={() => toggleDietaryTag(tag)}
                    className={`px-md py-sm rounded-full text-caption font-semibold transition-all ${
                      sel
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-secondary border border-text-tertiary/35 hover:border-primary/30 hover:text-text-primary'
                    }`}
                  >
                    {tag}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recipe tags */}
          <div>
            <FieldLabel>Recipe Tags</FieldLabel>
            <div className="flex flex-wrap gap-sm">
              {RECIPE_TAGS.map(({ value, label }) => {
                const sel = recipeTags.includes(value);
                return (
                  <button
                    key={value}
                    onClick={() => toggleRecipeTag(value)}
                    className={`px-md py-sm rounded-full text-caption font-semibold transition-all ${
                      sel
                        ? 'bg-primary text-white'
                        : 'bg-surface text-text-secondary border border-text-tertiary/35 hover:border-primary/30 hover:text-text-primary'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="border-t border-text-tertiary/10 pt-lg">
            <FieldLabel>Contains Allergens</FieldLabel>
            <p className="text-caption text-text-tertiary mb-md">Select all that apply.</p>
            {/* Allergen tags — error color when selected, matches iOS */}
            <div className="flex flex-wrap gap-sm">
              {ALLERGENS.map(a => {
                const sel = allergens.includes(a);
                return (
                  <button
                    key={a}
                    onClick={() => toggleAllergen(a)}
                    className={`px-md py-sm rounded-full text-caption font-semibold transition-all ${
                      sel
                        ? 'bg-error text-white'
                        : 'text-error border border-error/30 hover:bg-error/8'
                    }`}
                    style={!sel ? { background: 'rgba(var(--color-error-rgb),0.07)' } : {}}
                  >
                    {a}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </FormCard>

      {/* ── Ingredients ── */}
      <FormCard
        title="Ingredients *"
        icon={List}
        subtitle="Link ingredients to Nutrius catalog products"
      >
        <div className="space-y-sm">

          {ingredients.length > 0 && (
            <div className="space-y-sm">
              {ingredients.map(ing => (
                <div
                  key={ing.id}
                  className="flex items-center gap-md p-md rounded-large"
                  style={{ background: 'rgba(var(--color-primary-rgb),0.03)', border: '1px solid rgba(var(--color-primary-rgb),0.07)' }}
                >
                  {/* Product thumbnail or placeholder */}
                  <div
                    className="w-10 h-10 rounded-medium flex items-center justify-center flex-shrink-0 overflow-hidden"
                    style={{
                      background: ing.linkedProductImageURL
                        ? 'rgba(var(--color-primary-rgb),0.08)'
                        : 'rgba(var(--color-text-tertiary),0.07)',
                    }}
                  >
                    {ing.linkedProductImageURL ? (
                      <img src={ing.linkedProductImageURL} alt={ing.linkedProductName} className="w-full h-full object-contain p-xs" />
                    ) : (
                      <Package size={16} className="text-text-tertiary" style={{ opacity: 0.4 }} />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <span className="text-body text-text-primary">
                      {ing.amount || ing.quantity} {ing.unit} <span className="font-medium">{ing.name}</span>
                    </span>
                    {ing.notes && <span className="text-caption text-text-secondary ml-sm italic">{ing.notes}</span>}
                    {ing.linkedProductName && (
                      <p className="text-caption text-primary mt-xs flex items-center gap-xs">
                        <Link size={10} />{ing.linkedProductName}
                      </p>
                    )}
                  </div>

                  <button
                    onClick={() => ing.linkedProductId ? handleUnlinkProduct(ing.id) : setLinkingIngredientId(ing.id)}
                    className={`flex items-center gap-xs px-sm py-xs rounded-full text-caption font-semibold transition-all flex-shrink-0 ${
                      ing.linkedProductId
                        ? 'bg-primary/10 text-primary hover:bg-error/10 hover:text-error'
                        : 'bg-surface border border-text-tertiary/20 text-text-secondary hover:border-primary hover:text-primary'
                    }`}
                    title={ing.linkedProductId ? 'Unlink product' : 'Link to product'}
                  >
                    {ing.linkedProductId ? <Unlink size={12} /> : <Link size={12} />}
                    <span className="hidden sm:inline">{ing.linkedProductId ? 'Unlink' : 'Link'}</span>
                  </button>

                  <button
                    onClick={() => removeIngredient(ing.id)}
                    className="w-8 h-8 rounded-full bg-error/10 hover:bg-error/20 flex items-center justify-center transition-colors flex-shrink-0"
                  >
                    <Trash2 size={14} className="text-error" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Add ingredient inline form */}
          {showIngredientInput && (
            <div
              className="p-md rounded-large space-y-md"
              style={{ background: 'rgba(var(--color-surface),1)', border: '1px solid rgba(var(--color-text-tertiary),0.12)' }}
            >
              <div className="grid md:grid-cols-3 gap-md">
                <input type="text" value={ingredientName} onChange={e => setIngredientName(e.target.value)} placeholder="Ingredient name *" autoFocus className="px-md py-sm bg-white border border-text-tertiary/20 rounded-small focus:border-primary focus:outline-none text-body text-text-primary" />
                <input type="text" value={ingredientAmount} onChange={e => setIngredientAmount(e.target.value)} placeholder="Amount (e.g., 2) *" className="px-md py-sm bg-white border border-text-tertiary/20 rounded-small focus:border-primary focus:outline-none text-body text-text-primary" />
                <input type="text" value={ingredientUnit} onChange={e => setIngredientUnit(e.target.value)} placeholder="Unit (e.g., cups)" className="px-md py-sm bg-white border border-text-tertiary/20 rounded-small focus:border-primary focus:outline-none text-body text-text-primary" />
              </div>
              <input type="text" value={ingredientNotes} onChange={e => setIngredientNotes(e.target.value)} placeholder="Notes (optional, e.g., sifted)" className="w-full px-md py-sm bg-white border border-text-tertiary/20 rounded-small focus:border-primary focus:outline-none text-body text-text-primary" />
              <div className="flex gap-sm">
                <Button variant="outline" size="small" onClick={() => { setShowIngredientInput(false); setIngredientName(''); setIngredientAmount(''); setIngredientUnit(''); setIngredientNotes(''); }} className="flex-1">Cancel</Button>
                <Button variant="primary" size="small" onClick={addIngredient} disabled={!ingredientName.trim() || !ingredientAmount.trim()} className="flex-1">Add</Button>
              </div>
            </div>
          )}

          {/* Add ingredient button */}
          {!showIngredientInput && (
            <button
              onClick={() => setShowIngredientInput(true)}
              className="w-full flex items-center justify-center gap-sm py-md rounded-large text-caption font-semibold transition-all"
              style={{
                background: 'rgba(var(--color-primary-rgb),0.06)',
                border: '1px dashed rgba(var(--color-primary-rgb),0.2)',
                color: 'var(--color-primary)',
              }}
            >
              <Plus size={16} />
              Add Ingredient
            </button>
          )}

          {linkedCount > 0 && (
            <p className="text-caption text-primary flex items-center gap-xs">
              <Link size={11} />
              {linkedCount} of {ingredients.length} ingredients linked to catalog products
            </p>
          )}
        </div>
      </FormCard>

      {/* ── Instructions ── */}
      <FormCard
        title="Instructions *"
        icon={Hash}
        subtitle="Add a photo to each step so readers can follow along"
      >
        <div className="space-y-md">

          {steps.length > 0 && (
            <div className="space-y-md">
              {steps.map((step, index) => (
                <EditableStepCard
                  key={step.id}
                  step={step}
                  index={index}
                  total={steps.length}
                  entityId={entityId}
                  onUpdate={updateStep}
                  onRemove={removeStep}
                  onMoveUp={moveStep}
                  onMoveDown={(i) => moveStep(i, 1)}
                />
              ))}
            </div>
          )}

          {/* Add step inline form */}
          {showStepInput && (
            <div
              className="p-md rounded-large space-y-md"
              style={{ background: 'rgba(var(--color-surface),1)', border: '1px solid rgba(var(--color-text-tertiary),0.12)' }}
            >
              <p className="text-caption font-semibold text-text-secondary uppercase tracking-wide">Step {steps.length + 1}</p>
              <textarea
                value={stepInstruction}
                onChange={e => setStepInstruction(e.target.value)}
                placeholder="Describe this step..."
                rows={3}
                autoFocus
                className="w-full px-md py-sm bg-white border border-text-tertiary/20 rounded-small focus:border-primary focus:outline-none resize-none text-body text-text-primary"
              />
              <label className="flex items-center gap-sm cursor-pointer select-none">
                <input type="checkbox" checked={stepHasTimer} onChange={e => setStepHasTimer(e.target.checked)} className="accent-primary" />
                <Timer size={14} className="text-text-tertiary" />
                <span className="text-body text-text-primary">Add timer for this step</span>
              </label>
              {stepHasTimer && (
                <div className="flex items-center gap-sm">
                  <input type="number" value={stepTimer} onChange={e => setStepTimer(e.target.value)} placeholder="Minutes" min="1" className="w-32 px-md py-sm bg-white border border-text-tertiary/20 rounded-small focus:border-primary focus:outline-none text-body text-text-primary" />
                  <span className="text-caption text-text-tertiary">min</span>
                </div>
              )}
              <div className="flex gap-sm">
                <Button variant="outline" size="small" onClick={() => { setShowStepInput(false); setStepInstruction(''); setStepTimer(''); setStepHasTimer(false); }} className="flex-1">Cancel</Button>
                <Button variant="primary" size="small" onClick={addStep} disabled={!stepInstruction.trim()} className="flex-1">Add Step</Button>
              </div>
            </div>
          )}

          {!showStepInput && (
            <button
              onClick={() => setShowStepInput(true)}
              className="w-full flex items-center justify-center gap-sm py-md rounded-large text-caption font-semibold transition-all"
              style={{
                background: 'rgba(var(--color-primary-rgb),0.06)',
                border: '1px dashed rgba(var(--color-primary-rgb),0.2)',
                color: 'var(--color-primary)',
              }}
            >
              <Plus size={16} />
              Add Step
            </button>
          )}
        </div>
      </FormCard>

      {/* ── Nutrition ── */}
      <FormCard
        title="Nutrition"
        icon={BarChart2}
        subtitle="Optional — per serving. Leave blank if unknown."
      >
        <div className="grid md:grid-cols-2 gap-lg">
          {[
            { label: 'Calories',      unit: 'kcal', field: 'calories'      },
            { label: 'Protein',       unit: 'g',    field: 'protein'       },
            { label: 'Carbohydrates', unit: 'g',    field: 'carbohydrates' },
            { label: 'Fat',           unit: 'g',    field: 'fat'           },
            { label: 'Fiber',         unit: 'g',    field: 'fiber'         },
            { label: 'Sugar',         unit: 'g',    field: 'sugar'         },
            { label: 'Sodium',        unit: 'mg',   field: 'sodium'        },
          ].map(({ label, unit, field }) => (
            <div key={field}>
              <FieldLabel>{label} ({unit})</FieldLabel>
              <input
                type="number"
                value={nutrition[field] ?? ''}
                onChange={e => updateNutrition(field, e.target.value)}
                placeholder="—"
                min="0"
                step="0.1"
                className={INPUT_CLASS}
              />
            </div>
          ))}
        </div>
      </FormCard>

      {/* ── Error ── */}
      {error && (
        <div
          className="p-md rounded-large flex items-center gap-sm"
          style={{ background: 'rgba(var(--color-error-rgb),0.05)', border: '1px solid rgba(var(--color-error-rgb),0.2)' }}
        >
          <AlertTriangle size={16} className="text-error flex-shrink-0" />
          <p className="text-body text-error">{error}</p>
        </div>
      )}

      {/* ── Submit row — matches iOS submitRow ── */}
      <div className="flex gap-md">
        <Button variant="outline" onClick={onBack} className="flex-1">Cancel</Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={!isValid() || isSubmitting}
          className="flex-1"
        >
          {isSubmitting ? 'Submitting...' : isEditing ? 'Save Changes' : 'Submit Recipe'}
        </Button>
      </div>

      {/* ── Exit confirmation ── */}
      {showExitConfirm && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-lg">
          <Card className="max-w-sm w-full">
            <div className="text-center">
              <h3 className="text-title-3 font-bold text-text-primary mb-sm">Exit recipe?</h3>
              <p className="text-body text-text-secondary mb-lg">You have unsaved changes. Save a draft or discard them.</p>
              <div className="flex flex-col gap-sm">
                <Button variant="primary" className="w-full" onClick={() => { handleSaveDraft(); setShowExitConfirm(false); onBack(); }}>Save Draft & Exit</Button>
                <Button variant="outline" className="w-full" onClick={() => { deleteDraft(); onBack(); }}>Discard & Exit</Button>
                <Button variant="outline" className="w-full" onClick={() => setShowExitConfirm(false)}>Keep Editing</Button>
              </div>
            </div>
          </Card>
        </div>,
        document.body
      )}

      {/* ── Product Link Modal ── */}
      {linkingIngredientId && (
        <ProductLinkModal
          ingredientName={linkingIngredient?.name || ''}
          onLink={(product) => handleLinkProduct(linkingIngredientId, product)}
          onClose={() => setLinkingIngredientId(null)}
        />
      )}

    </div>
  );
};

export default RecipeSubmissionView;